import { createTheme, alpha } from '@mui/material/styles';

// Paleta cooperativista: verdes naturales, azules confianza, acentos cálidos
const palette = {
  primary: {
    main: '#1B5E20',
    light: '#4C8C4A',
    dark: '#003300',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#0D47A1',
    light: '#5472D3',
    dark: '#002171',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#2E7D32',
    light: '#60AD5E',
    dark: '#005005',
  },
  warning: {
    main: '#F9A825',
    light: '#FFD95A',
    dark: '#C17900',
  },
  error: {
    main: '#C62828',
    light: '#FF5F52',
    dark: '#8E0000',
  },
  info: {
    main: '#0288D1',
    light: '#5EB8FF',
    dark: '#005B9F',
  },
  background: {
    default: '#F5F7F5',
    paper: '#FFFFFF',
    dark: '#1A2F1A',
  },
  text: {
    primary: '#1A2F1A',
    secondary: '#4A5D4A',
    disabled: '#9E9E9E',
  },
  divider: 'rgba(27, 94, 32, 0.12)',
};

const theme = createTheme({
  palette,
  typography: {
    fontFamily: '"Nunito", "Segoe UI", sans-serif',
    h1: {
      fontFamily: '"Poppins", "Segoe UI", sans-serif',
      fontWeight: 700,
      fontSize: '2.5rem',
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Poppins", "Segoe UI", sans-serif',
      fontWeight: 600,
      fontSize: '2rem',
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Poppins", "Segoe UI", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h4: {
      fontFamily: '"Poppins", "Segoe UI", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h5: {
      fontFamily: '"Poppins", "Segoe UI", sans-serif',
      fontWeight: 500,
      fontSize: '1.1rem',
    },
    h6: {
      fontFamily: '"Poppins", "Segoe UI", sans-serif',
      fontWeight: 500,
      fontSize: '1rem',
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '0.95rem',
      color: palette.text.secondary,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.875rem',
      color: palette.text.secondary,
    },
    body1: {
      fontSize: '0.95rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      fontFamily: '"Poppins", "Segoe UI", sans-serif',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
    caption: {
      fontSize: '0.75rem',
      color: palette.text.secondary,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(27, 94, 32, 0.05)',
    '0px 4px 8px rgba(27, 94, 32, 0.08)',
    '0px 6px 12px rgba(27, 94, 32, 0.10)',
    '0px 8px 16px rgba(27, 94, 32, 0.12)',
    '0px 10px 20px rgba(27, 94, 32, 0.14)',
    '0px 12px 24px rgba(27, 94, 32, 0.16)',
    '0px 14px 28px rgba(27, 94, 32, 0.18)',
    '0px 16px 32px rgba(27, 94, 32, 0.20)',
    ...Array(16).fill('0px 16px 32px rgba(27, 94, 32, 0.20)'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: `${palette.primary.light} ${palette.background.default}`,
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: palette.background.default,
          },
          '&::-webkit-scrollbar-thumb': {
            background: palette.primary.light,
            borderRadius: '4px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(27, 94, 32, 0.25)',
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.2s ease-in-out',
        },
        contained: {
          background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.primary.dark} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${palette.primary.light} 0%, ${palette.primary.main} 100%)`,
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            backgroundColor: alpha(palette.primary.main, 0.04),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 20px rgba(27, 94, 32, 0.08)',
          border: '1px solid rgba(27, 94, 32, 0.06)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0px 8px 30px rgba(27, 94, 32, 0.12)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0px 2px 8px rgba(27, 94, 32, 0.06)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 3px rgba(27, 94, 32, 0.08)',
          backgroundImage: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          boxShadow: '2px 0px 12px rgba(27, 94, 32, 0.08)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: '4px 8px',
          '&.Mui-selected': {
            backgroundColor: alpha(palette.primary.main, 0.12),
            '&:hover': {
              backgroundColor: alpha(palette.primary.main, 0.18),
            },
            '& .MuiListItemIcon-root': {
              color: palette.primary.main,
            },
            '& .MuiListItemText-primary': {
              color: palette.primary.main,
              fontWeight: 600,
            },
          },
          '&:hover': {
            backgroundColor: alpha(palette.primary.main, 0.06),
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        filled: {
          '&.MuiChip-colorSuccess': {
            background: `linear-gradient(135deg, ${palette.success.light} 0%, ${palette.success.main} 100%)`,
          },
          '&.MuiChip-colorWarning': {
            background: `linear-gradient(135deg, ${palette.warning.light} 0%, ${palette.warning.main} 100%)`,
          },
          '&.MuiChip-colorError': {
            background: `linear-gradient(135deg, ${palette.error.light} 0%, ${palette.error.main} 100%)`,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.primary.light,
              },
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${alpha(palette.primary.main, 0.15)}`,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 600,
          backgroundColor: alpha(palette.primary.main, 0.04),
          color: palette.text.primary,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(palette.primary.main, 0.02),
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 500,
          textTransform: 'none',
          minHeight: 48,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 600,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: '0.8rem',
          backgroundColor: palette.background.dark,
        },
      },
    },
  },
});

export default theme;
